import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { GripVertical, Trash2 } from 'lucide-react';
import { type TemplateField } from './DynamicRequestForm';

const FIELD_TYPES = ['text', 'textarea', 'select', 'checkbox', 'user_picker', 'date', 'date_range'] as const;

interface TemplateFieldEditorProps {
    fields: TemplateField[];
    onChange: (fields: TemplateField[]) => void;
}

export function TemplateFieldEditor({ fields, onChange }: TemplateFieldEditorProps) {
    const addField = () => {
        const newField: TemplateField = {
            key: `field_${Date.now()}`,
            label: 'New Field',
            type: 'text',
            required: false,
            order: fields.length,
        };
        onChange([...fields, newField]);
    };

    const updateField = (index: number, updates: Partial<TemplateField>) => {
        const updated = [...fields];
        updated[index] = { ...updated[index], ...updates };
        onChange(updated);
    };

    const removeField = (index: number) => {
        onChange(fields.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-4">
            {fields.map((field, index) => (
                <div key={index} className="space-y-3 rounded-lg border p-4">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <GripVertical className="text-muted-foreground h-4 w-4 shrink-0" />
                            <span className="text-sm font-medium">{field.label || 'Unnamed field'}</span>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeField(index)}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                            <Label className="text-xs">Label</Label>
                            <Input value={field.label} onChange={(e) => updateField(index, { label: e.target.value })} placeholder="Field label" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Key (slug)</Label>
                            <Input
                                value={field.key}
                                onChange={(e) => updateField(index, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                                placeholder="field_key"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Type</Label>
                            <Select value={field.type} onValueChange={(v) => updateField(index, { type: v as TemplateField['type'] })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {FIELD_TYPES.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Placeholder</Label>
                            <Input
                                value={field.placeholder ?? ''}
                                onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                placeholder="Placeholder text"
                            />
                        </div>
                    </div>

                    {field.type === 'select' && (
                        <div className="space-y-1">
                            <Label className="text-xs">Options (one per line)</Label>
                            <Textarea
                                value={(field.options ?? []).join('\n')}
                                onChange={(e) => updateField(index, { options: e.target.value.split('\n').filter(Boolean) })}
                                rows={3}
                                placeholder="Option A&#10;Option B&#10;Option C"
                            />
                        </div>
                    )}

                    <div className="space-y-1">
                        <Label className="text-xs">Help text</Label>
                        <Input
                            value={field.help_text ?? ''}
                            onChange={(e) => updateField(index, { help_text: e.target.value })}
                            placeholder="Optional help text shown below the field"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id={`required-${index}`}
                            checked={field.required ?? false}
                            onCheckedChange={(checked) => updateField(index, { required: Boolean(checked) })}
                        />
                        <Label htmlFor={`required-${index}`} className="cursor-pointer text-xs">
                            Required
                        </Label>
                    </div>
                </div>
            ))}

            <Button type="button" variant="outline" className="w-full" onClick={addField}>
                + Add field
            </Button>
        </div>
    );
}
